import { getEvmClient } from '@1delta/providers'
import { Chain } from '@1delta/chain-registry'
import { apyToAprPercent } from '../../../utils/parsing'
import { YieldFetcher } from '../../types'

/**
 * Lista (slisBNB) BNB liquid-staking intrinsic yield.
 *
 * slisBNB is non-rebasing and value-accruing. Lista publishes the
 * staking APR (in percent) as a daily time series on its public
 * datachart API:
 *
 *   GET https://api.lista.org/api/datachart/history
 *       ?name=slisBNBRate&cycle=1&startTime=<sec>&endTime=<sec>
 *
 * The `amount` field on each point is the APR in percent (e.g. "1.24"),
 * `cycle=1` selects daily granularity. We take the most recent point.
 *
 * This replaces an earlier approach that sampled `convertSnBnbToBnb`
 * rate growth across a historical window: that required an archival BNB
 * node, and the public archival RPCs were too slow to serve the
 * historical reads within budget, so the fetcher almost always fell
 * back to 0. The API returns the published APR directly and reliably.
 *
 * `safeFetch` in the aggregator wraps this call, so a thrown error is
 * caught and the key is simply omitted rather than sinking the batch.
 */

const HISTORY_URL = 'https://api.lista.org/api/datachart/history'
/** Asset-group key — must match `yieldKey` in the LST registry entry. */
const SLISBNB = 'Lista Staked BNB::slisBNB'

const DAY_SECONDS = 86_400
/** Look-back window for the request — wide enough to always include
 *  recent daily points even if the series lags by a day or two. */
const LOOKBACK_SECONDS = 14 * DAY_SECONDS
const TIMEOUT_MS = 8_000

interface HistoryPoint {
  amount: string
  chartTime: number
}

interface HistoryResponse {
  code?: string
  msg?: string
  data?: HistoryPoint[]
}

export const slisBnbFetcher: YieldFetcher = {
  label: 'SLISBNB',
  fetch: async () => {
    const nowSec = Math.floor(Date.now() / 1000)
    const url =
      `${HISTORY_URL}?name=slisBNBRate&cycle=1` +
      `&startTime=${nowSec - LOOKBACK_SECONDS}&endTime=${nowSec}`

    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS)
    try {
      const res: HistoryResponse = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      }).then((r) => r.json())

      const points = res.data ?? []
      if (points.length === 0) return { [SLISBNB]: 0 }

      // Take the most recent point by timestamp; `amount` is APR percent.
      const latest = points.reduce((a, b) =>
        b.chartTime > a.chartTime ? b : a,
      )
      const apr = Number(latest.amount)
      return { [SLISBNB]: Number.isFinite(apr) && apr > 0 ? apr : 0 }
    } finally {
      clearTimeout(timer)
    }
  },
}

/**
 * Lista **lisAster** — ASTER locked-staking rewards, the collateral of the
 * `lisAster / ASTER` (94.5 % LLTV) loop market on Moolah.
 *
 * lisAster is NOT a rate-bearing LST. `AsterVault` (`0xb3Df1b69…`) takes
 * ASTER, locks it in Aster's own vault for the maximum 208 weeks with
 * auto-relock, and mints lisAster 1:1; the token is a plain ERC-20 with no
 * exchange rate and NO redemption path (exit is the DEX only). The reward
 * flow lives beside the token: Lista computes each weekly epoch's share
 * off-chain and publishes it as a cumulative Merkle root on
 * `LisAsterDistributor` (`0xeE545e1D…`), where it is claimed IN lisAster
 * (`claim` / `claimAndStake`) after a 6 h waiting period.
 *
 * Why it is still an intrinsic yield of the COLLATERAL: Lista's own FAQ
 * states that "lisAster supplied as collateral in the lisAster/Aster market,
 * or used as smart collateral in the lisAster&Aster/Aster Smart Lending
 * market, still counts as staked and earns rewards as normal" — and the
 * chain agrees. Only 2.88M of 4.69M lisAster sits in `LisAsterStaking`
 * (`0x3D786C99…`) while 1.81M sits in Moolah, and the epoch allocations
 * (7,160 / 6,216 / 8,551 lisAster for the weeks ending 2026-08-26 /
 * 09-02 / 09-09) only reconcile to Lista's published ~26 % against the
 * staked + Moolah base (32 % / 19 % / 21 % simple, on the start-of-epoch
 * base); against the staked-only base they read 42–50 %. So the rate is
 * earned by a Moolah depositor — in kind, proportional, but CLAIMED, not
 * accrued into the token price.
 *
 * Source is Lista's `/api/lisaster/overview`: `lisasterApy` is a FRACTION
 * (0.2633 = 26.33 %) and named as an APY, so it is converted to the
 * package's nominal-APR-percent convention. `soloStakingApy` on the same
 * response is what an unlocked solo staker earns and is not this asset.
 * The `datachart/history?name=lisAster*` series that serves slisBNB is
 * EMPTY for lisAster — do not reach for it.
 *
 * The `lisAster & ASTER-SmartLP` receipt (Moolah's two-coin StableSwap
 * provider `0x1cc913cd…`) is emitted too, at the pool's live lisAster
 * weight read from `balances(i)` on the dex — the FAQ extends the reward
 * to smart collateral, but only the lisAster leg of the pool is staked.
 * If that read fails the key is simply absent rather than guessed.
 */
const LISASTER_OVERVIEW_URL = 'https://api.lista.org/api/lisaster/overview'
/** Asset-group key — token-lists chain 56 spelling. */
export const LISASTER_KEY = 'Lista Staked Aster::lisAster'
export const LISASTER_SMARTLP_KEY =
  'lisAster & ASTER-SmartLP::lisAster & ASTER-SmartLP'
/** Lista's two-coin StableSwap behind the SmartLP provider: coins(0) = ASTER, coins(1) = lisAster. */
const LISASTER_SMARTLP_DEX =
  '0x510d69b25a2177eddce9becdb0a66a511c944840' as const
const STABLESWAP_BALANCES_ABI = [
  {
    type: 'function',
    name: 'balances',
    stateMutability: 'view',
    inputs: [{ name: 'i', type: 'uint256' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const

interface LisAsterOverview {
  code?: string
  data?: {
    lisasterApy?: string | number
    soloStakingApy?: string | number
    totalStakedAster?: string
    currentEpochId?: number
  }
}

export const lisAsterFetcher: YieldFetcher = {
  label: 'LISASTER',
  fetch: async () => {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS)
    let apr: number
    try {
      const res: LisAsterOverview = await fetch(LISASTER_OVERVIEW_URL, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      }).then((r) => r.json())
      const apyFraction = Number(res.data?.lisasterApy)
      if (!Number.isFinite(apyFraction) || apyFraction <= 0) {
        throw new Error(
          `lisAster: overview carried no usable lisasterApy (${String(res.data?.lisasterApy)})`,
        )
      }
      apr = apyToAprPercent(apyFraction * 100)
    } finally {
      clearTimeout(timer)
    }

    const out: Record<string, number> = { [LISASTER_KEY]: apr }

    // SmartLP leg weight — best effort, absent on failure.
    try {
      const client = getEvmClient(Chain.BNB_SMART_CHAIN_MAINNET)
      const [aster, lisAster] = await Promise.all([
        client.readContract({
          address: LISASTER_SMARTLP_DEX,
          abi: STABLESWAP_BALANCES_ABI,
          functionName: 'balances',
          args: [0n],
        }),
        client.readContract({
          address: LISASTER_SMARTLP_DEX,
          abi: STABLESWAP_BALANCES_ABI,
          functionName: 'balances',
          args: [1n],
        }),
      ])
      const total = aster + lisAster
      if (total > 0n) {
        const weight = Number(lisAster) / Number(total)
        out[LISASTER_SMARTLP_KEY] = apr * weight
      }
    } catch {
      // pool unreadable — leave the SmartLP key absent
    }
    return out
  },
}
